import { ImageProps } from 'next/image'
import {
  TcClickTypes,
  TcPageTemplateNames,
} from 'src/configuration/tagcommander'

import { ActionButtonProps } from '../../components/ActionButton'
import { colors } from '../../theme'
import { IconName, Icons } from '../../components/Icon'
import { Recipe, RecipeCategory } from '../../types/fam-graphql'
import { wysiwygTransformer } from '../../utils/DomUtils'
import { LinkProps } from '../../components/A'
import tcClick from '../tcClick/tcClick'
import { api } from '../../configuration'
import { getRecipeChildProfile } from '../Family/FamilyHelpers'
import { FamilyProfile } from '../../types'

import { nutritionalValuesTransformer } from './RecipeNutritionalValues'

function formatTimeText(args: {
  t: any
  time: number
  hoursKey: string
  minutesKey: string
}) {
  const { t, time, minutesKey, hoursKey } = args
  if (time < 60) {
    return t(minutesKey, { minutes: time })
  }
  return t(hoursKey, {
    hours: Math.floor(time / 60),
    minutes: `${time % 60}`.padStart(2, '0'),
  })
}

type RecipeIntroductionTransformerArgs = {
  recipe: Recipe | null
  t: any
  isSide: boolean
  selectedSide: string
  onSelectSwap?: (type: string) => void
  hideFavorite?: boolean
  isFavorite?: boolean
  onAddToFavorites?: {
    loginAction: () => void
    logoutAction: () => void
  }
  userLoggedIn?: boolean
  isFromPlanning?: boolean
  isFromHebdo?: boolean
  switchCourseAction?: LinkProps
  ssoLoginUrl?: string
  tcVars?: {
    chapter1: string
    chapter2: string
    chapter3: string
  }
  hasShare?: boolean
  isOffline?: boolean
}

const recipeIntroductionDefaultTransformer = (
  args: RecipeIntroductionTransformerArgs
) => {
  const {
    recipe,
    t,
    isSide,
    selectedSide,
    onSelectSwap,
    isFavorite,
    onAddToFavorites,
    isFromPlanning,
    isFromHebdo,
    switchCourseAction,
    userLoggedIn,
    ssoLoginUrl,
    tcVars,
    hideFavorite,
    hasShare,
    isOffline,
  } = args

  return {
    isCourseSide: isSide,
    swapCourseText: t('recipe_type_COURSE'),
    swapSideText: t('recipe_type_SIDE'),
    onSelectSwap,
    selectedSide,
    type:
      recipe?.recipe_category !== RecipeCategory.Dessert
        ? t(`recipe_type_${recipe?.recipe_category ?? ''}`)
        : t(`recipe_type_${recipe?.ranking ?? ''}`),
    switchButtonProps: isFromPlanning
      ? ({
          text: t('recipe_switch_cta'),
          icon: 'change' as IconName,
          variantType: 'link',
          variantColorScheme: 'colored',
          variantColor: colors.redMojo,
          disabled: isOffline,
          actionProps: {
            'aria-label': t('recipe_switch_label'),
            ...switchCourseAction,
          },
        } as ActionButtonProps)
      : isFromHebdo
      ? ({
          text: t('recipe_switch_cta'),
          icon: 'change' as IconName,
          variantType: 'link',
          variantColorScheme: 'colored',
          variantColor: colors.redMojo,
          disabled: isOffline,
          actionProps: {
            'aria-label': t('recipe_switch_label'),
            ...switchCourseAction,
          },
        } as ActionButtonProps)
      : undefined,
    favoriteButtonProps: hideFavorite
      ? null
      : ({
          text: '',
          icon: (isFavorite ? 'starFull' : 'star') as IconName,
          variantType: userLoggedIn ? 'button' : 'link',
          variantColor: colors.redMojo,
          variantIcon: 'only',
          variantColorScheme: 'iconRedMojo',
          disabled: isOffline,
          actionProps: {
            ...(userLoggedIn
              ? {}
              : {
                  href: ssoLoginUrl,
                  onClickHandler: onAddToFavorites?.logoutAction,
                }),
            onClick: () => {
              tcClick({
                click_name: `picto ajout favori_${TcPageTemplateNames.Recipe}_fam`,
                click_type: TcClickTypes.action,
                chapter1: tcVars?.chapter1 ?? '',
                chapter2: tcVars?.chapter2 ?? '',
                chapter3: tcVars?.chapter3 ?? '',
              })
              userLoggedIn && onAddToFavorites?.loginAction()
            },
            'aria-label': isFavorite
              ? t('recipe_remove_from_favorites')
              : t('recipe_add_to_favorites'),
          },
        } as ActionButtonProps),
    shareButtonProps: !isFromPlanning &&
      !isFromHebdo &&
      hasShare && {
        text: '',
        icon: 'share2' as IconName,
        variantType: 'button',
        variantColor: colors.redMojo,
        variantIcon: 'only',
        variantColorScheme: 'iconRedMojo',
        disabled: isOffline,
        actionProps: {
          onClick: () => {
            ;(navigator as any)
              ?.share({
                title: recipe?.name,
                text: t('recipe_sharing_text', { name: recipe?.name }),
                url: window.location.href,
              })
              .then(() => console.log('Successful share'))
              .catch((error: any) => console.log('Error sharing', error))
          },
        },
      },
    preparationText: recipe?.preparation_time
      ? formatTimeText({
          t,
          time: recipe?.preparation_time,
          minutesKey: 'recipe_preparation_text_min',
          hoursKey: 'recipe_preparation_text_hours',
        })
      : null,
    bakingText: recipe?.baking_time
      ? formatTimeText({
          t,
          time: recipe?.baking_time,
          minutesKey: 'recipe_baking_text_min',
          hoursKey: 'recipe_baking_text_hours',
        })
      : null,
    restText: recipe?.rest_time
      ? formatTimeText({
          t,
          time: recipe?.rest_time,
          minutesKey: 'recipe_rest_text_min',
          hoursKey: 'recipe_rest_text_hours',
        })
      : null,
    nutriscore: recipe?.nutriscore ?? '',
    aboutRecipeTitleText: t('recipe_about_title'),
    nutritionalValuesText: t('recipe_nutritional_values_cta'),
    ...nutritionalValuesTransformer(recipe, t),
    onNutritionalClick: () => {
      tcClick({
        click_name: `${t('recipe_nutritional_values_cta')}_fam`,
        click_type: TcClickTypes.action,
        chapter1: tcVars?.chapter1 ?? '',
        chapter2: tcVars?.chapter2 ?? '',
        chapter3: tcVars?.chapter3 ?? '',
      })
    },
    aboutRecipeText: wysiwygTransformer(recipe?.benefits ?? ''),
    imageProps: {
      src: recipe?.image
        ? recipe?.image
        : // FIXME: Hardcoded
          '/images/default/placeholder.png',
      alt: recipe?.image_alt ?? '',
      objectFit: 'cover',
      layout: 'fill',
      unoptimized: isOffline,
      fallbacksrc:
        api.API_URL +
        `/storage/recettes_generiques/${t(
          `recipe_type_${recipe?.recipe_category}_img_fallback`
        )}`,
    } as ImageProps,
  }
}

const recipeIntroductionForFamilyTransformer = (
  args: RecipeIntroductionTransformerArgs
) => {
  const defaultRecipeIntroduction = recipeIntroductionDefaultTransformer(args)

  const familyProfile = args.recipe
    ? getRecipeChildProfile(args.recipe)
    : FamilyProfile.CHILD_SMALL

  return {
    ...defaultRecipeIntroduction,
    tagAgeProps: {
      icon: {
        icon: Icons.familyStar as IconName,
      },
      text: args.t(`family_profile_${familyProfile}`),
      backgroundColor: colors.familyBlue,
    },
    highlightTitleProps: {
      text: defaultRecipeIntroduction.type,
      backgroundColor: colors.familyYellow,
      color: colors.familyBurgundy,
    },
  }
}

export const recipeIntroductionTransformer = (
  args: RecipeIntroductionTransformerArgs
) => {
  const { recipe } = args
  if (recipe?.family_recipe) {
    return recipeIntroductionForFamilyTransformer(args)
  } else {
    return recipeIntroductionDefaultTransformer(args)
  }
}
